import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from "antd";
import { EditOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { errorText, listFrom } from "./appraisalUtils";

const { Text } = Typography;

const AppraisalCriteria = ({ request, canManage = false }) => {
  const [form] = Form.useForm();
  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formError, setFormError] = useState("");
  const [editor, setEditor] = useState({ open: false, record: null });
  const mountedRef = useRef(false);
  const loadSequenceRef = useRef(0);
  const writeLockRef = useRef(false);

  const load = useCallback(async () => {
    if (!mountedRef.current) return;
    const sequence = ++loadSequenceRef.current;
    setLoading(true);
    setError("");
    try {
      const data = await request("GET", "criteria");
      if (mountedRef.current && sequence === loadSequenceRef.current) {
        setCriteria(listFrom(data));
      }
    } catch (loadError) {
      if (mountedRef.current && sequence === loadSequenceRef.current) {
        setError(errorText(loadError));
      }
    } finally {
      if (mountedRef.current && sequence === loadSequenceRef.current) {
        setLoading(false);
      }
    }
  }, [request]);

  useEffect(() => {
    mountedRef.current = true;
    load();
    return () => {
      mountedRef.current = false;
      loadSequenceRef.current += 1;
    };
  }, [load]);

  const openEditor = (record = null) => {
    if (!canManage || writeLockRef.current) return;
    setError("");
    setFormError("");
    form.resetFields();
    setEditor({ open: true, record });
    form.setFieldsValue({
      key: record?.key || "",
      name: record?.name || "",
      description: record?.description || "",
      sortOrder: record?.sortOrder ?? 0,
      isActive: record ? Boolean(record.isActive) : true,
      maxRating: record?.maxRating ?? record?.max_rating ?? null,
    });
  };

  const closeEditor = () => {
    if (writeLockRef.current) return;
    setEditor({ open: false, record: null });
    setFormError("");
    form.resetFields();
  };

  const save = async () => {
    if (!canManage || writeLockRef.current) return;
    writeLockRef.current = true;
    try {
      const values = await form.validateFields();
      if (!mountedRef.current) return;
      setSaving(true);
      setFormError("");
      setError("");
      const record = editor.record;
      if (
        !values.isActive &&
        record?.isActive &&
        criteria.filter((item) => item.isActive).length <= 1
      ) {
        setFormError("At least one appraisal criterion must remain active.");
        return;
      }
      const payload = record
        ? {
            name: values.name.trim(),
            description: values.description?.trim() || "",
            sortOrder: values.sortOrder,
            isActive: values.isActive,
          }
        : {
            key: values.key.trim().toLowerCase().replace(/\s+/g, "_"),
            name: values.name.trim(),
            description: values.description?.trim() || "",
            sortOrder: values.sortOrder ?? 0,
            isActive: values.isActive ?? true,
          };
      if (values.maxRating != null) {
        const originalMax = record?.maxRating ?? record?.max_rating ?? null;
        if (!record || values.maxRating !== originalMax) {
          payload.maxRating = values.maxRating;
        }
      }

      try {
        await request(
          record ? "PATCH" : "POST",
          record ? `criteria/${record.id}` : "criteria",
          payload,
        );
      } catch (writeError) {
        if (mountedRef.current) setFormError(errorText(writeError));
        return;
      }

      if (mountedRef.current) {
        setEditor({ open: false, record: null });
        form.resetFields();
      }
      await load();
    } catch (saveError) {
      if (!saveError?.errorFields && mountedRef.current) {
        setFormError(errorText(saveError));
      }
    } finally {
      writeLockRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const changeStatus = async (record, isActive) => {
    if (!canManage || writeLockRef.current) return;
    if (
      !isActive &&
      record.isActive &&
      criteria.filter((item) => item.isActive).length <= 1
    ) {
      setError("At least one appraisal criterion must remain active.");
      return;
    }
    writeLockRef.current = true;
    setSaving(true);
    setError("");
    try {
      await request("PATCH", `criteria/${record.id}`, { isActive });
      await load();
    } catch (writeError) {
      if (mountedRef.current) setError(errorText(writeError));
    } finally {
      writeLockRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const remove = async (record) => {
    if (!canManage || writeLockRef.current) return;
    if (
      record.isActive &&
      criteria.filter((item) => item.isActive).length <= 1
    ) {
      setError("At least one appraisal criterion must remain active.");
      return;
    }
    writeLockRef.current = true;
    setSaving(true);
    setError("");
    try {
      await request("DELETE", `criteria/${record.id}`);
      await load();
    } catch (deleteError) {
      if (mountedRef.current) setError(errorText(deleteError));
    } finally {
      writeLockRef.current = false;
      if (mountedRef.current) setSaving(false);
    }
  };

  const columns = [
    {
      title: "Key",
      dataIndex: "key",
      width: 170,
      render: (value) => <Text code>{value}</Text>,
    },
    { title: "Name", dataIndex: "name" },
    {
      title: "Description",
      dataIndex: "description",
      render: (value) => value || "—",
    },
    { title: "Order", dataIndex: "sortOrder", width: 90 },
    {
      title: "Max",
      dataIndex: "maxRating",
      width: 80,
      render: (value) => value ?? 20,
    },
    { title: "Version", dataIndex: "version", width: 90 },
    {
      title: "Status",
      dataIndex: "isActive",
      width: 110,
      render: (active) => (
        <Tag color={active ? "green" : "default"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    ...(canManage
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 230,
            render: (_, record) => (
              <Space wrap>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  disabled={saving}
                  onClick={() => openEditor(record)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  disabled={saving}
                  onClick={() => changeStatus(record, !record.isActive)}
                >
                  {record.isActive ? "Deactivate" : "Reactivate"}
                </Button>
                <Popconfirm
                  title="Delete this criterion?"
                  description="Referenced criteria cannot be deleted. Deactivate the criterion if it is referenced."
                  okText="Delete"
                  cancelText="Cancel"
                  onConfirm={() => remove(record)}
                  disabled={saving}
                >
                  <Button size="small" danger disabled={saving}>
                    Delete
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {error && <Alert type="error" showIcon message={error} />}
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Text type="secondary">Active and inactive appraisal criteria</Text>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={load}
            loading={loading || saving}
          >
            Refresh
          </Button>
          {canManage && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openEditor()}
              disabled={saving}
            >
              Add criterion
            </Button>
          )}
        </Space>
      </Space>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={criteria}
        loading={loading}
        pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        scroll={{ x: 850 }}
      />
      <Modal
        title={editor.record ? "Edit criterion" : "Add appraisal criterion"}
        open={editor.open}
        onCancel={closeEditor}
        footer={[
          <Button key="cancel" onClick={closeEditor} disabled={saving}>
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={saving} onClick={save}>
            Save
          </Button>,
        ]}
        closable={!saving}
        maskClosable={!saving}
        keyboard={!saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          {formError && (
            <Alert
              type="error"
              showIcon
              message={formError}
              style={{ marginBottom: 16 }}
            />
          )}
          <Form.Item
            name="key"
            label="Unique key"
            rules={
              editor.record
                ? []
                : [
                    {
                      required: true,
                      whitespace: true,
                      message: "Enter a unique key.",
                    },
                    {
                      pattern: /^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/,
                      message:
                        "Use letters, numbers, spaces, underscores, or hyphens.",
                    },
                  ]
            }
            extra={
              editor.record
                ? "The key stays fixed; change the name to rename this criterion."
                : "Spaces are converted to underscores; keys are saved in lowercase."
            }
          >
            <Input maxLength={50} disabled={saving || !!editor.record} />
          </Form.Item>
          <Form.Item
            name="name"
            label="Name"
            rules={[
              { required: true, whitespace: true, message: "Enter a name." },
            ]}
          >
            <Input maxLength={100} disabled={saving} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} disabled={saving} />
          </Form.Item>
          <Form.Item
            name="sortOrder"
            label="Display order"
            rules={[{ required: true, message: "Enter a display order." }]}
          >
            <InputNumber
              min={0}
              precision={0}
              style={{ width: "100%" }}
              disabled={saving}
            />
          </Form.Item>
          <Form.Item
            name="maxRating"
            label="Max rating"
            extra="Optional. Leave empty to use the default (20)."
          >
            <InputNumber
              min={1}
              precision={0}
              style={{ width: "100%" }}
              disabled={saving}
              placeholder="Default (20)"
            />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch disabled={saving} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default AppraisalCriteria;
